import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import axios from 'axios';
import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { SearchLg, Trash01 } from '../../assets';
import { Franchise, Store } from '../../app/services/api/generated';
import { ListPageTemplate } from '../../components/page-templates/list-page/ListPageTemplate';
import { AuditHistoryPanel } from '../../components/shared/audit/AuditHistoryPanel';
import { CloseDialogAction } from '../../components/shared/dialogs/CloseDialogAction';
import { ConfirmationDialog } from '../../components/shared/dialogs/ConfirmationDialog';
import { EmptyTable } from '../../components/shared/EmptyTable';
import MessagesSnackbar from '../../components/shared/MessagesSnackbar';
import { useConfigurations } from '../../components/shared/useConfigurations';
import { UserContext } from '../../components/shared/useUser';
import { ConfigurationName } from '../../enums/ConfigurationName';
import useLogError from '../../hooks/useLogError';
import useNswagClient, {
  ManagedConfiguration,
  ManagedConfigurationWrite,
  UnclassifiedConfiguration,
} from '../../hooks/api/useNswagClient';
import { BrandingSelector } from './components/BrandingSelector';
import { localisedDescription, localisedName, useConfigurationTableSection } from './components/ConfigurationTable';
import { PosIntegrationDialog } from './components/PosIntegrationDialog';

const ALL = '';
const VALUE_TYPES = ['Number', 'Text', 'Boolean', 'Dropdown'] as const;

type Feedback = { message: string; severity: 'success' | 'error' | 'warning' };

type PendingWrite = { value?: string; removeOverride: boolean; clearSecret: boolean; resetToDefault: boolean };

const ManageConfigurations = () => {
  const { t } = useTranslation('manageConfigurations');
  const { selectedStore, hasPermissionTo } = useContext(UserContext);
  const { logError } = useLogError();
  const { invalidateConfigurations, isConfigurationEnabled } = useConfigurations();
  const {
    getFranchises,
    getFranchiseStores,
    getManagedConfigurations,
    getUnclassifiedConfigurations,
    getConfigurationAudit,
    saveManagedConfiguration,
  } = useNswagClient();

  const canManage = hasPermissionTo(['ConfigurationAdmin']);
  const canManageBranding = hasPermissionTo(['FranchiseDynamicBrandingWrite'])
    && isConfigurationEnabled(ConfigurationName.FranchiseDynamicBranding);

  const [franchises, setFranchises] = useState<Franchise[]>([]);
  const [stores, setStores] = useState<Store[]>([]);
  const [franchise, setFranchise] = useState<Franchise | null>(null);
  const [store, setStore] = useState<Store | null>(null);
  const [items, setItems] = useState<ManagedConfiguration[]>([]);
  const [unclassified, setUnclassified] = useState<UnclassifiedConfiguration[]>([]);
  const [loading, setLoading] = useState(false);
  const [savingName, setSavingName] = useState<string>();
  const [retries, setRetries] = useState<Record<string, PendingWrite>>({});
  const [feedback, setFeedback] = useState<Feedback | null>(null);

  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState(ALL);
  const [customerManageable, setCustomerManageable] = useState(ALL);
  const [valueType, setValueType] = useState(ALL);
  const [settableScope, setSettableScope] = useState(ALL);

  const [auditItem, setAuditItem] = useState<ManagedConfiguration>();
  const [specialistItem, setSpecialistItem] = useState<ManagedConfiguration>();
  const [secretToClear, setSecretToClear] = useState<ManagedConfiguration>();

  const scope = store ? 'Store' : 'Franchise';

  // Every asynchronous result is checked against the scope generation it was requested in. The
  // generation advances on each scope change, so a response from before the user left a scope is
  // discarded even if they have since come back to it.
  const generationRef = useRef(0);
  const stillIn = (generation: number) => generationRef.current === generation;

  const reportError = (reason: unknown, message: string) => {
    // An Axios error carries the request body, which for a secret write is the typed secret.
    logError(axios.isAxiosError(reason) ? { status: reason.response?.status, url: reason.config?.url, message: reason.message } : reason);
    setFeedback({ message, severity: 'error' });
  };

  const isConflict = (reason: unknown) => axios.isAxiosError(reason) && reason.response?.status === 409;

  const loadScope = (franchiseName: string, storeNumber?: string) =>
    Promise.all([
      getManagedConfigurations(franchiseName, storeNumber),
      getUnclassifiedConfigurations(franchiseName, storeNumber),
    ]);

  // The server refused a stale revision: show what it holds now so the next save starts from it.
  const reloadAfterConflict = async (reason: unknown, startedIn: number) => {
    if (!stillIn(startedIn)) return;
    reportError(reason, t('conflictError'));
    // Whatever warned before the conflict was written against a row someone else has since changed;
    // replaying it now would apply the old operation on top of their value.
    setRetries({});
    if (!franchise?.franchiseName) return;
    try {
      const [managed, unknownKeys] = await loadScope(franchise.franchiseName, store?.storeNumber);
      if (!stillIn(startedIn)) return;
      setItems(managed);
      setUnclassified(unknownKeys);
      clearDrafts();
    } catch (loadFailure) {
      logError(loadFailure);
    }
  };

  useEffect(() => {
    if (!canManage) return;
    void getFranchises()
      .then(result => {
        const options = result.data ?? [];
        setFranchises(options);
        const active = options.find(option => option.franchiseName === selectedStore?.franchiseName)
          ?? (options.length === 1 ? options[0] : null);
        setFranchise(active);
      })
      .catch(reason => reportError(reason, t('loadError')));
  }, [canManage]);

  useEffect(() => {
    setStore(null);
    setStores([]);
    if (!franchise?.franchiseName) return;
    let stale = false;
    void getFranchiseStores(franchise.franchiseName)
      .then(result => { if (!stale) setStores(result.data?.stores ?? []); })
      .catch(reason => { if (!stale) reportError(reason, t('loadError')); });
    return () => { stale = true; };
  }, [franchise?.franchiseName]);

  const categories = useMemo(
    () => Array.from(new Set(items.map(item => item.category))).sort((left, right) => left.localeCompare(right)),
    [items],
  );

  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();

    return items.filter(item => {
      const searchable = `${localisedName(t, item)} ${localisedDescription(t, item)} ${item.category} ${item.name}`.toLowerCase();
      if (term && !searchable.includes(term)) return false;
      if (category !== ALL && item.category !== category) return false;
      if (customerManageable === 'yes' && !item.isCustomerManageable) return false;
      if (customerManageable === 'no' && item.isCustomerManageable) return false;
      if (valueType !== ALL && item.valueType !== valueType) return false;
      if (settableScope === 'franchise' && !item.canSetAtFranchise) return false;
      if (settableScope === 'store' && !item.canSetAtStore) return false;
      return true;
    });
  }, [items, search, category, customerManageable, valueType, settableScope, t]);

  const replaceItem = (saved: ManagedConfiguration) =>
    setItems(current => current.map(configuration => configuration.name === saved.name ? saved : configuration));

  // The write has committed by now. A failed client refresh is reported as that, never as a failed
  // save, and Save stays available to run the refresh again; the server writes nothing for a repeat.
  const refreshAfterSave = async (item: ManagedConfiguration, write: PendingWrite, startedIn: number) => {
    try {
      await invalidateConfigurations();
    } catch (reason) {
      logError(reason);
      if (!stillIn(startedIn)) return;
      setRetries(current => ({ ...current, [item.name]: write }));
      setFeedback({ message: t('refreshWarning'), severity: 'warning' });
    }
  };

  const save = async (
    item: ManagedConfiguration,
    value?: string,
    removeOverride = false,
    clearSecret = false,
    resetToDefault = false,
  ) => {
    if (!franchise?.franchiseName) return;
    setSavingName(item.name);
    const startedIn = generationRef.current;

    const request: ManagedConfigurationWrite = {
      name: item.name,
      franchiseName: franchise.franchiseName,
      storeNumber: store?.storeNumber,
      value,
      expectedRevision: item.revision,
      removeOverride,
      clearSecret,
      confirmSecretClear: clearSecret,
      resetToDefault,
    };

    try {
      const saved = await saveManagedConfiguration(request);
      if (!stillIn(startedIn)) return;
      replaceItem(saved.item);
      clearDraft(item.name);
      // A retry would hold the submitted secret in client state and send it again; the active client
      // configuration never contains a secret either, so a secret write ends here.
      const holdsSecret = item.isSecret && !clearSecret;
      setRetries(current => {
        const next = { ...current };
        if (saved.warnings.length > 0 && !holdsSecret) next[item.name] = { value, removeOverride, clearSecret, resetToDefault };
        else delete next[item.name];
        return next;
      });
      setFeedback(saved.warnings.length > 0
        ? { message: `${t('propagationWarning')} ${saved.warnings.join(' ')}`, severity: 'warning' }
        : { message: t('saveSuccess', { name: localisedName(t, item) }), severity: 'success' });
      if (!holdsSecret) await refreshAfterSave(item, { value, removeOverride, clearSecret, resetToDefault }, startedIn);
    } catch (reason) {
      if (isConflict(reason)) await reloadAfterConflict(reason, startedIn);
      else if (stillIn(startedIn)) reportError(reason, t('saveError'));
    } finally {
      if (stillIn(startedIn)) setSavingName(undefined);
    }
  };

  const { table, clearDraft, clearDrafts, resetPage } = useConfigurationTableSection({
    title: t('tableTitle'),
    scope,
    items: filtered,
    loading,
    savingName,
    retryNames: useMemo(() => new Set(Object.keys(retries)), [retries]),
    onRetry: item => {
      const write = retries[item.name];
      if (write) void save(item, write.value, write.removeOverride, write.clearSecret, write.resetToDefault);
    },
    recordCountLabel: t('recordCountLabel'),
    emptyContent: franchise ? (
      <EmptyTable
        text={t('emptyTitle')}
        searchTerm={search}
        onClear={() => clearFilters()}
        onAdd={() => undefined}
        addItemText=""
        onAddIsCreating={false}
        showOnAdd={false}
        withPaginationShown={false}
      />
    ) : (
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: 240,
          textAlign: 'center',
        }}
      >
        <Typography
          variant="textMD"
          color="text.secondary"
        >
          {t('selectFranchisePrompt')}
        </Typography>
      </Box>
    ),
    showCustomerManageable: true,
    onSave: (item, value, removeOverride) => void save(item, value, removeOverride),
    onResetToDefault: item => void save(item, undefined, false, false, true),
    onClearSecret: setSecretToClear,
    onViewAudit: setAuditItem,
    onConfigureSpecialist: setSpecialistItem,
    cleanupStaleOverrides: true,
  });

  const applySearch = () => {
    setSearch(searchInput.trim());
    resetPage();
  };

  const clearSearch = () => {
    setSearchInput('');
    setSearch('');
    resetPage();
  };

  const clearFilters = () => {
    clearSearch();
    setCategory(ALL);
    setCustomerManageable(ALL);
    setValueType(ALL);
    setSettableScope(ALL);
  };

  const filterBy = (setter: (value: string) => void) => (value: string) => {
    setter(value);
    resetPage();
  };

  // A new scope means a new set of values: anything typed for the previous one must not carry over,
  // and a response that arrives for a scope the user has since left must not be shown as current.
  useEffect(() => {
    generationRef.current += 1;
    clearDrafts();
    resetPage();
    setSavingName(undefined);
    setRetries({});
    setSecretToClear(undefined);
    setAuditItem(undefined);
    // The previous scope's rows must never stay on screen: if this load fails they would be edited
    // and saved against the new scope.
    setItems([]);
    setUnclassified([]);
    if (!franchise?.franchiseName) {
      setLoading(false);
      return;
    }
    let stale = false;
    setLoading(true);
    void loadScope(franchise.franchiseName, store?.storeNumber)
      .then(([managed, unknownKeys]) => {
        if (stale) return;
        setItems(managed);
        setUnclassified(unknownKeys);
      })
      .catch(reason => { if (!stale) reportError(reason, t('loadError')); })
      .finally(() => { if (!stale) setLoading(false); });
    return () => { stale = true; };
  }, [franchise?.franchiseName, store?.storeNumber]);

  if (!canManage) return null;

  const filterContent = (
    <Grid
      container
      spacing={5}
    >
      <Grid
        item
        lg={3}
        sm={6}
        xs={12}
      >
        <TextField
          fullWidth
          label={t('search')}
          placeholder={t('searchPlaceholder')}
          value={searchInput}
          disabled={!franchise}
          onChange={event => setSearchInput(event.target.value)}
          onKeyDown={event => event.key === 'Enter' && applySearch()}
        />
      </Grid>
      <Grid
        item
        lg={2}
        sm={3}
        xs={6}
      >
        <Button
          fullWidth
          variant="secondary"
          size="lg"
          disabled={!franchise}
          onClick={clearSearch}
        >
          {t('clear')}
        </Button>
      </Grid>
      <Grid
        item
        lg={2}
        sm={3}
        xs={6}
      >
        <Button
          fullWidth
          variant="primary"
          size="lg"
          disabled={!franchise}
          startIcon={<SvgIcon><SearchLg /></SvgIcon>}
          onClick={applySearch}
        >
          {t('search')}
        </Button>
      </Grid>
      <Grid
        item
        lg={2.5}
        sm={6}
        xs={12}
      >
        <Autocomplete
          options={franchises}
          value={franchise}
          onChange={(_, selected) => setFranchise(selected)}
          getOptionLabel={option => option.franchiseName ?? ''}
          isOptionEqualToValue={(option, selected) => option.franchiseName === selected.franchiseName}
          renderInput={params => (
            <TextField
              {...params}
              label={t('franchise')}
              placeholder={t('selectFranchise')}
            />
          )}
        />
      </Grid>
      <Grid
        item
        lg={2.5}
        sm={6}
        xs={12}
      >
        <Autocomplete
          options={stores}
          value={store}
          disabled={!franchise}
          onChange={(_, selected) => setStore(selected)}
          getOptionLabel={option => option.storeName ?? option.storeNumber ?? ''}
          isOptionEqualToValue={(option, selected) => option.storeNumber === selected.storeNumber}
          renderInput={params => (
            <TextField
              {...params}
              label={t('storeOverrideScope')}
              placeholder={t('franchiseDefault')}
            />
          )}
        />
      </Grid>
      {canManageBranding && franchise?.franchiseId !== undefined && franchise.franchiseName && (
        <Grid
          item
          xs={12}
        >
          <BrandingSelector
            franchiseId={franchise.franchiseId}
            franchiseName={franchise.franchiseName}
          />
        </Grid>
      )}
      {unclassified.length > 0 && (
        <Grid
          item
          xs={12}
        >
          <Alert severity="warning">
            <AlertTitle>{t('unclassifiedTitle')}</AlertTitle>
            {t('unclassifiedDescription')}
            {' '}
            {unclassified.map(configuration => `${configuration.name} (${configuration.rowCount})`).join(', ')}
          </Alert>
        </Grid>
      )}
    </Grid>
  );

  const selectFilter = (id: string, label: string, value: string, onChange: (value: string) => void, allLabel: string, options: { value: string; label: string }[]) => (
    <Grid
      item
      lg={3}
      sm={6}
      xs={12}
    >
      <FormControl fullWidth>
        <InputLabel
          id={`${id}-label`}
          shrink
        >
          {label}
        </InputLabel>
        <Select
          labelId={`${id}-label`}
          value={value}
          label={label}
          displayEmpty
          notched
          onChange={event => onChange(event.target.value)}
        >
          <MenuItem value={ALL}>{allLabel}</MenuItem>
          {options.map(option => (
            <MenuItem
              key={option.value}
              value={option.value}
            >
              {option.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>
    </Grid>
  );

  const advancedFilterContent = (
    <Grid
      container
      spacing={5}
    >
      {selectFilter('configuration-category', t('filters.category'), category, filterBy(setCategory), t('filters.allCategories'),
        categories.map(name => ({ value: name, label: name })))}
      {selectFilter('configuration-customer-manageable', t('filters.customerManageable'), customerManageable, filterBy(setCustomerManageable), t('filters.any'),
        [{ value: 'yes', label: t('yes') }, { value: 'no', label: t('no') }])}
      {selectFilter('configuration-value-type', t('valueTypes.label'), valueType, filterBy(setValueType), t('filters.any'),
        VALUE_TYPES.map(type => ({ value: type, label: t(`valueTypes.${type}`) })))}
      {selectFilter('configuration-settable-scope', t('filters.settableScope'), settableScope, filterBy(setSettableScope), t('filters.any'),
        [{ value: 'franchise', label: t('filters.settableAtFranchise') }, { value: 'store', label: t('filters.settableAtStore') }])}
    </Grid>
  );

  return (
    <>
      <ListPageTemplate<ManagedConfiguration>
        title={t('title')}
        subtitle={t('pageSubtitle')}
        filterContent={filterContent}
        advancedFilterContent={advancedFilterContent}
        advancedFilterActions={(
          <Button
            variant="secondary"
            size="md"
            onClick={clearFilters}
          >
            {t('filters.clear')}
          </Button>
        )}
        table={table}
      />
      <Dialog
        open={Boolean(auditItem)}
        onClose={() => setAuditItem(undefined)}
        fullWidth
        maxWidth="lg"
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {auditItem ? t('audit.title', { name: localisedName(t, auditItem) }) : ''}
          <CloseDialogAction onCancel={() => setAuditItem(undefined)} />
        </DialogTitle>
        <DialogContent>
          {auditItem && franchise?.franchiseName && (
            <AuditHistoryPanel
              key={`${auditItem.name}|${franchise.franchiseName}|${store?.storeNumber ?? ''}`}
              permission="ConfigurationAdmin"
              title={t(`audit.scope${scope}`)}
              fetchEntries={({ action, search: term, skip, take }) => getConfigurationAudit({
                name: auditItem.name,
                franchiseName: franchise.franchiseName as string,
                storeNumber: store?.storeNumber,
                action,
                search: term,
                skip,
                take,
              })}
            />
          )}
        </DialogContent>
      </Dialog>
      <ConfirmationDialog
        open={Boolean(secretToClear)}
        icon={<Trash01 />}
        variant="bad"
        title={secretToClear ? t('clearSecretTitle', { name: localisedName(t, secretToClear) }) : ''}
        message={t('clearSecretMessage')}
        confirmButtonText={t('actions.clearSecret')}
        cancelButtonText={t('cancel')}
        isProcessing={savingName !== undefined && savingName === secretToClear?.name}
        onConfirm={() => {
          const item = secretToClear;
          setSecretToClear(undefined);
          if (item) void save(item, undefined, false, true);
        }}
        onCancel={() => setSecretToClear(undefined)}
      />
      <PosIntegrationDialog
        item={specialistItem}
        saving={savingName === specialistItem?.name}
        onApply={value => {
          const item = specialistItem;
          setSpecialistItem(undefined);
          if (item) void save(item, value);
        }}
        onClose={() => setSpecialistItem(undefined)}
      />
      <MessagesSnackbar
        open={Boolean(feedback)}
        onClose={() => setFeedback(null)}
        message={feedback?.message ?? ''}
        severity={feedback?.severity ?? 'success'}
        duration={feedback?.severity === 'success' ? 4000 : null}
      />
    </>
  );
};

export default ManageConfigurations;
